import { useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  ChevronDownIcon,
  DropdownMenu,
  ToggleGroup,
  Tooltip,
  UpgradeBadge,
} from '@buffer-mono/popcorn'

import {
  DATE_RANGE_OPTIONS,
  calculateDateRange,
  formatDateRange,
} from '../../utils'
import { DateRangeDrawer, DateRangePopover } from '../DateRangePicker'
import { InsightsUpgradeDialog } from '../InsightsUpgradeDialog'

import styles from './DateRangeToggle.module.css'

interface DateRangeToggleProps {
  selectedDateRange: string
  onDateRangeChange: (value: string) => void
  currentRange: { startDate: Date; endDate: Date }
  onCustomRangeApply: (start: Date, end: Date) => void
  weekStartsOn?: 0 | 1
  showUpgradePath?: boolean
  onCustomBlocked?: () => void
  disabled?: boolean
}

interface DateRangeLayoutProps extends DateRangeToggleProps {
  dateRangeOptions: typeof DATE_RANGE_OPTIONS
  /** Active range label for the Custom item, or null on a preset. */
  customTooltip: string | null
}

/** Resolves a preset value to its current date range label. */
function tooltipForPreset(value: string): string {
  const { startDate, endDate } = calculateDateRange(value)
  return formatDateRange(startDate, endDate)
}

/**
 * Date-range selector. Renders both a segmented ToggleGroup (wide) and a compact
 * dropdown menu (narrow) and swaps them with a container query, so the layout
 * follows the available content width rather than the viewport — correct even
 * when the sidebar narrows the content while the window stays wide. The compact
 * menu uses Popcorn's `DropdownMenu`, which becomes a bottom-sheet drawer on
 * small screens, matching the comparison and other filter controls. Each form
 * owns its own picker state; only the visible one is interactive, so they never
 * cross-fire.
 */
export function DateRangeToggle(props: DateRangeToggleProps): JSX.Element {
  const { selectedDateRange, currentRange, showUpgradePath = false } = props

  // Free-tier users are capped at 30 days of history. "Last month" reaches up to
  // ~62 days back (previous full month + current month), so hide it from the
  // restricted experience — the same signal that gates "Custom". The server
  // independently clamps free accounts' range to 31 days.
  const dateRangeOptions = showUpgradePath
    ? DATE_RANGE_OPTIONS.filter((option) => option.value !== 'last-month')
    : DATE_RANGE_OPTIONS

  // Show the active range on the Custom item only when the user has actually
  // picked a custom range; on a preset, `currentRange` mirrors that preset and
  // would just duplicate its tooltip, so we omit it there.
  const customTooltip =
    selectedDateRange === 'custom'
      ? formatDateRange(currentRange.startDate, currentRange.endDate)
      : null

  return (
    <>
      <div className={styles.wide}>
        <DateRangeToggleGroup
          {...props}
          dateRangeOptions={dateRangeOptions}
          customTooltip={customTooltip}
        />
      </div>
      <div className={styles.narrow}>
        <DateRangeMenu
          {...props}
          dateRangeOptions={dateRangeOptions}
          customTooltip={customTooltip}
        />
      </div>
    </>
  )
}

/**
 * Compact form, shown when the container is too narrow for the toggle. Uses
 * Popcorn's `DropdownMenu`, which renders an anchored menu on wide viewports and
 * a bottom-sheet drawer on small ones — matching the sibling comparison and tag
 * controls. Its radio items fire on every click (unlike a native Select), so
 * re-picking "Custom" reliably reopens the picker with no extra bookkeeping.
 */
function DateRangeMenu({
  selectedDateRange,
  onDateRangeChange,
  currentRange,
  onCustomRangeApply,
  weekStartsOn,
  showUpgradePath = false,
  onCustomBlocked,
  dateRangeOptions,
  disabled = false,
}: DateRangeLayoutProps): JSX.Element {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false)

  const triggerLabel =
    dateRangeOptions.find((option) => option.value === selectedDateRange)
      ?.label ?? 'Custom'

  const handleSelect = (value: string): void => {
    // Close the menu explicitly — in DropdownMenu's drawer mode (small screens)
    // Radix's select-to-close is a no-op, so a pick would leave the drawer open.
    setIsMenuOpen(false)
    if (value === 'custom') {
      if (showUpgradePath) {
        setIsUpgradeDialogOpen(true)
      } else {
        // Defer the URL mutation to Apply; just open the picker here.
        setIsDatePickerOpen(true)
      }
      return
    }
    onDateRangeChange(value)
  }

  const handleUpgradeClick = (): void => {
    setIsUpgradeDialogOpen(false)
    onCustomBlocked?.()
  }

  return (
    <>
      <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <DropdownMenu.Trigger>
          <Button
            variant="secondary"
            size="medium"
            disabled={disabled}
            aria-label={`Date range: ${triggerLabel}`}
          >
            {triggerLabel}
            <ChevronDownIcon size="xsmall" color="subtle" />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="start">
          <DropdownMenu.RadioGroup
            value={selectedDateRange}
            onChange={handleSelect}
          >
            {dateRangeOptions.map((option) => (
              <DropdownMenu.RadioItem key={option.value} value={option.value}>
                {option.label}
              </DropdownMenu.RadioItem>
            ))}
            <DropdownMenu.RadioItem value="custom">
              Custom
              {showUpgradePath && <UpgradeBadge size="small" />}
            </DropdownMenu.RadioItem>
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu>
      <DateRangeDrawer
        open={isDatePickerOpen}
        onOpenChange={setIsDatePickerOpen}
        initialRange={currentRange}
        weekStartsOn={weekStartsOn}
        onApply={onCustomRangeApply}
      />
      {showUpgradePath && (
        <InsightsUpgradeDialog
          open={isUpgradeDialogOpen}
          onOpenChange={setIsUpgradeDialogOpen}
          onUpgradeClick={handleUpgradeClick}
        />
      )}
    </>
  )
}

/** Segmented toggle form, shown when the container is wide enough. */
function DateRangeToggleGroup({
  selectedDateRange,
  onDateRangeChange,
  currentRange,
  onCustomRangeApply,
  weekStartsOn,
  showUpgradePath = false,
  onCustomBlocked,
  dateRangeOptions,
  customTooltip,
  disabled = false,
}: DateRangeLayoutProps): JSX.Element {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false)

  const handleUpgradeClick = (): void => {
    setIsUpgradeDialogOpen(false)
    onCustomBlocked?.()
  }

  return (
    <ToggleGroup
      className={styles.toggleGroup}
      disabled={disabled}
      value={selectedDateRange}
      onChange={(value) => {
        if (value) {
          if (value === 'custom' && showUpgradePath) {
            return
          }
          onDateRangeChange(value)
          setIsDatePickerOpen(value === 'custom')
        }
      }}
    >
      {dateRangeOptions.map((option) => (
        <ToggleGroup.Item key={option.value} value={option.value}>
          <Tooltip content={tooltipForPreset(option.value)}>
            <span>{option.label}</span>
          </Tooltip>
        </ToggleGroup.Item>
      ))}
      {disabled ? (
        <span>
          <ToggleGroup.Item value="custom" key="custom" disabled>
            Custom
            {showUpgradePath && <UpgradeBadge size="small" />}
          </ToggleGroup.Item>
        </span>
      ) : showUpgradePath ? (
        <InsightsUpgradeDialog
          open={isUpgradeDialogOpen}
          onOpenChange={setIsUpgradeDialogOpen}
          onUpgradeClick={handleUpgradeClick}
          trigger={
            <ToggleGroup.Item value="custom" key="custom" tabIndex={-1}>
              {customTooltip ? (
                <Tooltip content={customTooltip}>
                  <span className={styles.mutedLabel}>Custom</span>
                </Tooltip>
              ) : (
                <span className={styles.mutedLabel}>Custom</span>
              )}{' '}
              <UpgradeBadge size="small" />
            </ToggleGroup.Item>
          }
        />
      ) : (
        <DateRangePopover
          open={isDatePickerOpen}
          onOpenChange={setIsDatePickerOpen}
          initialRange={currentRange}
          weekStartsOn={weekStartsOn}
          onApply={onCustomRangeApply}
        >
          <ToggleGroup.Item value="custom" key="custom" tabIndex={-1}>
            {customTooltip ? (
              <Tooltip content={customTooltip}>
                <span>Custom</span>
              </Tooltip>
            ) : (
              <span>Custom</span>
            )}
          </ToggleGroup.Item>
        </DateRangePopover>
      )}
    </ToggleGroup>
  )
}
